import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';

interface PurchaseOrderRow {
  id: number;
  po_number: string | null;
  supplier_id: number;
  order_state: 'draft' | 'submitted' | 'partial' | 'received' | 'cancelled';
  currency: string;
  ordered_date: string | null;
  expected_date: string | null;
  status: string;
}

// order_state drives the PO lifecycle; tag tone makes the terminal states scannable.
// Non-terminal states use the neutral base tag (only ok/warn modifiers exist).
function stateCell(state: PurchaseOrderRow['order_state']) {
  const mod = state === 'received' ? ' records__tag--ok' : state === 'cancelled' ? ' records__tag--warn' : '';
  return <span className={`records__tag${mod}`}>{state}</span>;
}

const COLUMNS: Column<PurchaseOrderRow>[] = [
  { key: 'po_number', header: 'PO #', render: (r) => r.po_number ?? 'draft' },
  { key: 'supplier_id', header: 'Supplier', numeric: true, sortable: true },
  { key: 'order_state', header: 'State', render: (r) => stateCell(r.order_state) },
  { key: 'currency', header: 'Currency' },
  { key: 'ordered_date', header: 'Ordered', sortable: true, sortAccessor: (r) => r.ordered_date ?? null, render: (r) => r.ordered_date ?? '—' },
  { key: 'expected_date', header: 'Expected ETA', sortable: true, sortAccessor: (r) => r.expected_date ?? null, render: (r) => r.expected_date ?? '—' },
];

const FILTERS: FilterDef<PurchaseOrderRow>[] = [
  {
    key: 'state',
    label: 'State',
    options: [
      { value: 'all', label: 'All' },
      { value: 'draft', label: 'Draft' },
      { value: 'submitted', label: 'Submitted' },
      { value: 'partial', label: 'Partial' },
      { value: 'received', label: 'Received' },
      { value: 'cancelled', label: 'Cancelled' },
    ],
    match: (r, v) => r.order_state === v,
  },
];

export function PurchaseOrders() {
  return (
    <RecordsTable<PurchaseOrderRow>
      title="Purchase Orders"
      intro="Manage all supplier purchase orders from creation to fulfillment. Each order moves through four stages: Draft → Submitted → Partial → Received."
      tool="listPurchaseOrders"
      args={{ limit: 200 }}
      select={(r) => (r as { purchaseOrders: PurchaseOrderRow[] }).purchaseOrders ?? []}
      columns={COLUMNS}
      rowKey={(r) => r.id}
      emptyMessage="No purchase orders yet. They appear once suppliers are loaded and POs are raised."
      refreshable
      searchable={{
        placeholder: 'Search by PO # or supplier id…',
        match: (r, q) =>
          [r.po_number, String(r.supplier_id)].some((f) => f != null && f.toLowerCase().includes(q)),
      }}
      filters={FILTERS}
    />
  );
}
